import { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2, Plus, Expand } from "lucide-react";
import { toast } from "sonner";
import { CreateDesignDisciplineDialog } from "./CreateDesignDisciplineDialog";
import { EditDescriptionModal } from "./EditDescriptionModal";

interface DisciplineUpdate {
  name: string;
  abbreviation: string | null;
  group_code: string | null;
  description: string | null;
}

// The Estonian short codes, in table order. A group code replaces the
// abbreviation on discipline pills (KT, VN and JH all show KVJ). The English
// codes sit beside `name_en` in DisciplinesTranslationsTable.
const CODE_FIELDS = [
  { field: "abbreviation", label: "Abbreviation", placeholder: "e.g. LIFT" },
  { field: "group_code", label: "Group code", placeholder: "e.g. KVJ" },
] as const;

const DesignDisciplinesTable = observer(() => {
  const { designDisciplinesStore } = useStores();
  const [disciplineUpdates, setDisciplineUpdates] = useState<Record<number, DisciplineUpdate>>({});
  const [savingDiscipline, setSavingDiscipline] = useState<number | null>(null);
  const [deletingDiscipline, setDeletingDiscipline] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [disciplineToDelete, setDisciplineToDelete] = useState<{ id: number; name: string } | null>(
    null
  );
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [editingDisciplineId, setEditingDisciplineId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);

  useEffect(() => {
    designDisciplinesStore.loadDisciplines();
  }, [designDisciplinesStore]);

  useEffect(() => {
    // Initialize local state with current tags
    const updates: Record<number, DisciplineUpdate> = {};
    designDisciplinesStore.disciplinesList.forEach((tag) => {
      updates[tag.id] = {
        name: tag.name,
        abbreviation: tag.abbreviation,
        group_code: tag.group_code,
        description: tag.description,
      };
    });
    setDisciplineUpdates(updates);
  }, [designDisciplinesStore.disciplinesList]);

  const handleSave = async (tagId: number) => {
    const update = disciplineUpdates[tagId];
    if (!update) return;

    setSavingDiscipline(tagId);
    try {
      await designDisciplinesStore.updateDiscipline(tagId, update);
      toast.success("Design discipline updated successfully");
    } catch (error) {
      toast.error("Failed to update design discipline");
    } finally {
      setSavingDiscipline(null);
    }
  };

  const handleDeleteClick = useCallback((tagId: number, tagName: string) => {
    setDisciplineToDelete({ id: tagId, name: tagName });
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!disciplineToDelete) return;

    setDeletingDiscipline(disciplineToDelete.id);
    try {
      await designDisciplinesStore.deleteDiscipline(disciplineToDelete.id);
      toast.success("Design discipline deleted successfully");
    } catch (error) {
      toast.error("Failed to delete design discipline");
    } finally {
      setDeletingDiscipline(null);
      setDeleteConfirmOpen(false);
      setDisciplineToDelete(null);
    }
  }, [disciplineToDelete, designDisciplinesStore]);

  const handleFieldChange = (
    tagId: number,
    field: keyof DisciplineUpdate,
    value: string | null
  ) => {
    setDisciplineUpdates((prev) => ({
      ...prev,
      [tagId]: {
        ...prev[tagId],
        [field]: value,
      },
    }));
  };

  // Filter tags based on search query (memoized for performance)
  // MUST be before early returns to comply with React Rules of Hooks
  const filteredDisciplines = useMemo(() => {
    if (!searchQuery.trim()) {
      return designDisciplinesStore.disciplinesList;
    }

    const query = searchQuery.toLowerCase();
    return designDisciplinesStore.disciplinesList.filter((tag) => {
      return (
        tag.name.toLowerCase().includes(query) ||
        CODE_FIELDS.some(({ field }) => tag[field]?.toLowerCase().includes(query)) ||
        (tag.description && tag.description.toLowerCase().includes(query))
      );
    });
  }, [designDisciplinesStore.disciplinesList, searchQuery]);

  // Early returns AFTER all hooks
  if (designDisciplinesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (designDisciplinesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading design disciplines: {designDisciplinesStore.error}
      </div>
    );
  }

  if (filteredDisciplines.length === 0) {
    return (
      <>
        <div className="mb-4 flex items-center justify-between">
          <Input
            placeholder="Search design disciplines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Discipline
          </Button>
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim()
            ? "No design disciplines match your search."
            : "No design disciplines found. Click the button above to create your first tag."}
        </div>
        <CreateDesignDisciplineDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search design disciplines..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Discipline
        </Button>
      </div>

      <div className="rounded-md border">
        <Table className="w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "25%" }}>Discipline Name</TableHead>
              {CODE_FIELDS.map(({ field, label }) => (
                <TableHead key={field} style={{ width: "10%" }}>
                  {label}
                </TableHead>
              ))}
              <TableHead style={{ width: "40%" }}>Description</TableHead>
              <TableHead style={{ width: "15%" }} className="text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDisciplines.map((tag) => {
              const update = disciplineUpdates[tag.id];
              if (!update) return null;

              const hasChanges =
                update.name !== tag.name ||
                CODE_FIELDS.some(({ field }) => update[field] !== tag[field]) ||
                update.description !== tag.description;

              return (
                <TableRow key={tag.id}>
                  <TableCell style={{ width: "25%" }}>
                    <Input
                      value={update.name}
                      onChange={(e) => handleFieldChange(tag.id, "name", e.target.value)}
                      className="h-8 w-full"
                    />
                  </TableCell>
                  {CODE_FIELDS.map(({ field, label, placeholder }) => (
                    <TableCell key={field} style={{ width: "10%" }}>
                      <Input
                        value={update[field] || ""}
                        onChange={(e) => handleFieldChange(tag.id, field, e.target.value || null)}
                        placeholder={placeholder}
                        aria-label={`${label} — ${tag.name}`}
                        className="h-8 w-full"
                      />
                    </TableCell>
                  ))}
                  <TableCell style={{ width: "40%" }}>
                    <div className="flex items-center gap-1">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {update.description || (
                          <span className="italic text-muted-foreground">No description</span>
                        )}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={() => {
                          setEditingDisciplineId(tag.id);
                          setEditingDescription(update.description);
                          setIsDescriptionModalOpen(true);
                        }}
                        title="Edit description"
                      >
                        <Expand className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell style={{ width: "15%" }} className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSave(tag.id)}
                        disabled={!hasChanges || savingDiscipline === tag.id}
                      >
                        {savingDiscipline === tag.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteClick(tag.id, tag.name)}
                        disabled={deletingDiscipline === tag.id}
                      >
                        {deletingDiscipline === tag.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CreateDesignDisciplineDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Design Discipline"
        description={`Are you sure you want to delete "${disciplineToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />

      <EditDescriptionModal
        open={isDescriptionModalOpen}
        onOpenChange={setIsDescriptionModalOpen}
        title="Edit Discipline Description"
        description={editingDescription}
        itemName={
          editingDisciplineId !== null ? disciplineUpdates[editingDisciplineId]?.name : undefined
        }
        onSave={(newDescription) => {
          if (editingDisciplineId !== null) {
            handleFieldChange(editingDisciplineId, "description", newDescription);
          }
        }}
      />
    </div>
  );
});

export default DesignDisciplinesTable;
